import i18n from '@translations/i18n';
import {
  IFeedResourceEndpointParams,
  IPaginatedResourceEndpointParams,
} from './interfaces';

/** Backend validation responses must use these codes (not human-readable text). */
const API_VALIDATION_ERROR_I18N: Record<string, string> = {
  'score_submission.daily_limit_reached':
    'competitions:portal.dialogs.submitScore.errors.dailyLimitReached',
  'score_submission.outside_competition_period':
    'competitions:portal.dialogs.submitScore.errors.outsideCompetitionPeriod',
  'score_submission.cannot_delete_non_pending':
    'competitions:portal.yourScores.delete.errors.cannotDeleteNonPending',
  'score_submission.weapon_group_required':
    'competitions:portal.dialogs.submitScore.errors.weaponGroupRequired',
  'score_submission.weapon_group_invalid':
    'competitions:portal.dialogs.submitScore.errors.weaponGroupInvalid',
  'score_submission.weapon_group_clearance_required':
    'competitions:portal.dialogs.submitScore.errors.weaponGroupClearanceRequired',
};

export const getApiUrl = (): string => {
  return import.meta.env.VITE_API_HOST;
};

export const getPaginatedEndpointParams = ({
  page,
  perPage,
  sort,
  search,
  filters,
}: IPaginatedResourceEndpointParams): Record<
  string,
  string | number | boolean
> => {
  const params: Record<string, string | number | boolean> = {
    page,
    perPage,
  };

  if (sort) {
    params.sort = `${sort.field};${sort.sort}`;
  }

  if (search) {
    params.search = search;
  }

  if (filters) {
    const filterObject: Record<
      string,
      | { operator: string; value: string | number | boolean }
      | Array<string | number | boolean>
    > = {};

    filters.forEach((filter) => {
      if ('values' in filter) {
        filterObject[filter.field] = filter.values;
      } else {
        filterObject[filter.field] = {
          operator: filter.operator,
          value: filter.value,
        };
      }
    });

    params.filters = encodeURIComponent(JSON.stringify(filterObject));
  }

  return params;
};

export const getFeedEndpointParams = ({
  perPage,
  sort,
  search,
  filters,
  cursor,
}: IFeedResourceEndpointParams): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {
    perPage,
  };

  if (cursor) {
    params.cursor = cursor;
  }

  if (sort) {
    params.sort = `${sort.field};${sort.sort}`;
  }

  if (search) {
    params.search = search;
  }

  if (filters) {
    const filterObject: Record<
      string,
      | { operator: string; value: string | number | boolean }
      | Array<string | number | boolean>
    > = {};

    filters.forEach((filter) => {
      if ('values' in filter) {
        filterObject[filter.field] = filter.values;
      } else {
        filterObject[filter.field] = {
          operator: filter.operator,
          value: filter.value,
        };
      }
    });

    params.filters = encodeURIComponent(JSON.stringify(filterObject));
  }

  return params;
};

export const getValidationErrorKey = (error: string): string | undefined => {
  if (error === 'The email has already been taken.') {
    return 'emailTaken';
  }

  if (error === 'The name has already been taken.') {
    return 'nameTaken';
  }

  return undefined;
};

export const translateApiValidationError = (
  code: string | undefined
): string | undefined => {
  if (!code) {
    return undefined;
  }

  const i18nKey = API_VALIDATION_ERROR_I18N[code];

  if (i18nKey) {
    return i18n.t(i18nKey);
  }

  const legacyKey = getValidationErrorKey(code);

  if (legacyKey) {
    return i18n.t(`errors.${legacyKey}`);
  }

  return undefined;
};
